import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import { Phonenumber } from 'data/firestore/agent/customer/phonenumber/types';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { formatPhoneNumber } from 'data/services/phone/formatter';
import getE164Number from 'data/services/phone/getE164Number';
import { CREATE_EDIT_CUSTOMER_PHONE_NUMBER_DRAWER, CUSTOMER_CALL_MODAL } from '../../constants';

interface FormValues extends Record<string, unknown> {
    phoneNumber: Record<string, unknown>;
    phoneType: Record<string, string>;
    note?: string;
}

const getPhoneValue = (value?: {
    countryCode?: string;
    isoCode?: string;
    phone?: string;
}) => (value?.phone ? getE164Number(`${value?.countryCode}${value?.phone}`) : '');

const mapFormValuesToData = (values: FormValues) => {
    const { phoneNumber, phoneType, ...rest } = values;

    return {
        ...rest,
        phoneType: phoneType?.value,
        phoneNumber: getPhoneValue(phoneNumber),
    };
};

const mapDataToFormValues = (phoneNumberInfo?: Phonenumber | null) => {
    if (phoneNumberInfo) {
        const {
            phoneNumber,
            phoneType,
            note,
        } = phoneNumberInfo;

        return {
            phoneNumber: formatPhoneNumber(phoneNumber as string),
            phoneType: {
                label: phoneType,
                value: phoneType,
            },
            note,
        } as Record<string, unknown>;
    }
    return undefined;
};

const useManageCustomerFallbackNumbers = ({
    agentId,
    customerId,
    onPhoneCreated = () => {},
    onPhoneDeleted = () => {},
} : {
    agentId: string;
    customerId: string;
    onPhoneCreated?: (phone: Phonenumber) => void
    onPhoneDeleted?: () => void;
}) => {
    const [openForm, closeForm] = useOpenModal(CREATE_EDIT_CUSTOMER_PHONE_NUMBER_DRAWER);
    const [openCustomerCallModal] = useOpenModal(CUSTOMER_CALL_MODAL);
    const queryClient = useQueryClient();

    const { mutateAsync: createPhoneNumber } = useHttpMutation({
        method: 'POST',
        pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers`,
        onSuccess: () => {
            closeForm();
            queryClient.invalidateQueries({
                queryKey: ['customer-phone-numbers', customerId],
                refetchActive: true,
            });
        },
    });

    const { mutateAsync: updatePhoneNumber } = useHttpMutation({
        method: 'PATCH',
        onSuccess: () => {
            closeForm();
            queryClient.invalidateQueries({
                queryKey: ['customer-phone-numbers', customerId],
                refetchActive: true,
            });
        },
    });

    const { mutateAsync: promotePhoneNumber } = useHttpMutation({
        method: 'POST',
        onSuccess: () => {
            closeForm();
            queryClient.invalidateQueries({
                queryKey: ['customer-phone-numbers', customerId],
                refetchActive: true,
            });
        },
    });

    const { mutateAsync: deletePhoneNumber } = useHttpMutation({
        method: 'DELETE',
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['customer-phone-numbers', customerId],
                refetchActive: true,
            });
        },
    });

    const { mutateAsync: verifyPhoneNumber } = useHttpMutation({
        method: 'PUT',
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['customer-phone-numbers', customerId],
                refetchActive: true,
            });
        },
    });

    const handleCreatePhoneNumber = useCallback(
        () => openForm({
            header: 'Add a Phone Number',
            mode: 'create',
            agentId,
            onSubmit: async (values: FormValues) => {
                showAppSpinner();
                try {
                    const phone = await createPhoneNumber({
                        body: mapFormValuesToData(values),
                    }) as Phonenumber;
                    showAppNotification({
                        title: 'Success',
                        description: 'Phone added successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                    onPhoneCreated(phone);
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [agentId, createPhoneNumber, onPhoneCreated, openForm],
    );

    const handleEditPhoneNumber = useCallback(
        (data: Phonenumber) => openForm({
            header: 'Edit Phone Number',
            mode: 'edit',
            initialValues: mapDataToFormValues(data),
            onSubmit: async (values: FormValues) => {
                showAppSpinner();
                try {
                    await updatePhoneNumber({
                        pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers/${data.id}`,
                        body: mapFormValuesToData(values),
                    });
                    showAppNotification({
                        title: 'Success',
                        description: 'Your changes have been saved.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [openForm, updatePhoneNumber, agentId, customerId],
    );

    const handleDeletePhoneNumber = useCallback(
        async (phoneId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'destructive',
                question: (
                    <ModalBody
                        variant="destructive"
                        icon={<Trash />}
                        title="Remove phone number?"
                        description="Once this action is confirmed, it cannot be undone."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Remove',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await deletePhoneNumber({
                    pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers/${phoneId}`,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'Phone deleted successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
                onPhoneDeleted();
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [deletePhoneNumber, agentId, customerId, onPhoneDeleted],
    );

    const handlePromotePhoneNumber = useCallback(
        async (phoneId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'neutral',
                question: (
                    <ModalBody
                        variant="default"
                        title="Promote phone number?"
                        description="Promoting this number will make it the primary contact method."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Set as primary',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await promotePhoneNumber({
                    pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers/${phoneId}/promote`,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'Phone promoted successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [promotePhoneNumber, agentId, customerId],
    );

    const handleVerifyPhoneNumber = useCallback(
        async (phoneId: string, value: boolean) => {
            const result = await confirmModal({
                header: '',
                variant: 'brand',
                question: (
                    value ? (
                        <ModalBody
                            variant="default"
                            title="Verify phone number?"
                            description="Marking this phone number as verified will set this customer as the default for incoming calls from it. This will override any other customer currently verified with this number."
                        />
                    ) : (
                        <ModalBody
                            variant="default"
                            title="Unverify phone number?"
                            description="Marking this phone number as unverified will unset this customer as the default for incoming calls."
                        />
                    )
                ),
                borderRadius: 'semi-square',
                okButtonLabel: value ? 'Mark as verified' : 'Unmark as verified',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await verifyPhoneNumber({
                    pathname: `/agents/${agentId}/customers/${customerId}/phone-numbers/${phoneId}/verify`,
                    body: { value, replacePolicy: 'override' },
                });
                showAppNotification({
                    title: 'Success',
                    description: value ? 'Phone number marked as verified.' : 'Phone number unmarked as verified.',
                    icon: 'success',
                    timeout: 5000,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [verifyPhoneNumber, agentId, customerId],
    );

    return useMemo(() => ({
        create: handleCreatePhoneNumber,
        edit: handleEditPhoneNumber,
        remove: handleDeletePhoneNumber,
        callPhone: (phone: Phonenumber) => openCustomerCallModal({
            customer: customerId,
            phoneNumber: phone,
        }),
        closeForm,
        promote: handlePromotePhoneNumber,
        verify: handleVerifyPhoneNumber,
    }), [
        closeForm,
        customerId,
        handleCreatePhoneNumber,
        handleDeletePhoneNumber,
        handlePromotePhoneNumber,
        handleEditPhoneNumber,
        handleVerifyPhoneNumber,
        openCustomerCallModal,
    ]);
};

export default useManageCustomerFallbackNumbers;
